// components/home/Announcements.jsx
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaBullhorn, 
  FaCalendarAlt, 
  FaExclamationTriangle, 
  FaStethoscope,
  FaHeartbeat,
  FaSyringe,
  FaUserMd,
  FaClinicMedical,
  FaFirstAid,
  FaNotesMedical,
  FaChevronLeft,
  FaChevronRight,
  FaClock,
  FaMapMarkerAlt
} from 'react-icons/fa';
import { GiMedicines } from 'react-icons/gi';
import { format } from 'date-fns';

const Announcements = () => {
    const [activeCategory, setActiveCategory] = useState('all');
    const [currentPage, setCurrentPage] = useState(0);
    
    // Clinic-specific announcements for Dr. Asrat Medical Clinic
    const announcements = [
        {
            id: 1,
            icon: <FaStethoscope className="text-xl text-blue-600" />,
            title: "Extended OPD Hours",
            description: "Outpatient department now open from 8:00 AM to 8:00 PM, Monday to Saturday for your convenience.",
            date: new Date(2024, 11, 15),
            category: "timings",
            priority: "high",
            badge: "New",
            color: "border-blue-500 bg-blue-50",
            location: "Main Building, Ground Floor"
        },
        {
            id: 2,
            icon: <FaCalendarAlt className="text-xl text-green-600" />,
            title: "Free Health Checkup Camp",
            description: "Free diabetes and hypertension screening camp on January 25, 2025. All are welcome from 9 AM to 4 PM.",
            date: new Date(2024, 11, 10),
            category: "camp",
            priority: "medium",
            badge: "Free Service",
            color: "border-green-500 bg-green-50",
            location: "Clinic Campus"
        },
        {
            id: 3,
            icon: <FaExclamationTriangle className="text-xl text-red-600" />,
            title: "Flu Season Alert",
            description: "Increased flu cases reported. Masks recommended in waiting areas. Vaccination available at pharmacy.",
            date: new Date(2024, 11, 5),
            category: "alert",
            priority: "high",
            badge: "Important",
            color: "border-red-500 bg-red-50",
            location: "All Departments"
        },
        {
            id: 4,
            icon: <FaHeartbeat className="text-xl text-purple-600" />,
            title: "New ECG Machine Installed",
            description: "Advanced digital ECG machine now available for faster and more accurate cardiac diagnosis.",
            date: new Date(2024, 10, 28),
            category: "facility",
            priority: "medium",
            badge: "New Equipment",
            color: "border-purple-500 bg-purple-50",
            location: "Cardiology Department"
        },
        {
            id: 5,
            icon: <GiMedicines className="text-xl text-amber-600" />,
            title: "Pharmacy Stock Update",
            description: "All essential medicines now available. New stock of pediatric medicines arrived.",
            date: new Date(2024, 10, 20),
            category: "pharmacy",
            priority: "low",
            badge: "Updated",
            color: "border-amber-500 bg-amber-50",
            location: "24/7 Pharmacy"
        },
        {
            id: 6,
            icon: <FaUserMd className="text-xl text-cyan-600" />,
            title: "New Specialist Joining",
            description: "Dr. Selam Tesfaye, Pediatric Specialist, joining our team from January 1, 2025.",
            date: new Date(2024, 10, 15),
            category: "staff",
            priority: "medium",
            badge: "New Doctor",
            color: "border-cyan-500 bg-cyan-50",
            location: "Pediatrics Department"
        },
        {
            id: 7,
            icon: <FaSyringe className="text-xl text-emerald-600" />,
            title: "Vaccination Drive",
            description: "Childhood immunization drive from Dec 20-30. All vaccines as per national schedule available.",
            date: new Date(2024, 10, 10),
            category: "camp",
            priority: "high",
            badge: "Ongoing",
            color: "border-emerald-500 bg-emerald-50",
            location: "Vaccination Room"
        },
        {
            id: 8,
            icon: <FaNotesMedical className="text-xl text-indigo-600" />,
            title: "Medical Certificate Process",
            description: "Medical certificates now processed within 2 working days. Bring government ID for verification.",
            date: new Date(2024, 10, 5),
            category: "service",
            priority: "medium",
            badge: "Process Update",
            color: "border-indigo-500 bg-indigo-50",
            location: "Registration Desk"
        },
        {
            id: 9,
            icon: <FaFirstAid className="text-xl text-rose-600" />,
            title: "Emergency Services Enhancement",
            description: "Emergency department now equipped with additional monitoring equipment and trained staff.",
            date: new Date(2024, 9, 28),
            category: "facility",
            priority: "high",
            badge: "Enhanced",
            color: "border-rose-500 bg-rose-50",
            location: "Emergency Department"
        },
        {
            id: 10,
            icon: <FaClinicMedical className="text-xl text-violet-600" />,
            title: "Family Planning Services",
            description: "Comprehensive family planning services available with counseling. Confidential and affordable.",
            date: new Date(2024, 9, 20),
            category: "service",
            priority: "medium",
            badge: "Available",
            color: "border-violet-500 bg-violet-50",
            location: "Women's Health Clinic"
        }
    ];

    const categories = [
        { id: 'all', label: 'All Announcements', count: announcements.length },
        { id: 'alert', label: 'Health Alerts', count: announcements.filter(a => a.category === 'alert').length },
        { id: 'timings', label: 'Timing Updates', count: announcements.filter(a => a.category === 'timings').length },
        { id: 'camp', label: 'Medical Camps', count: announcements.filter(a => a.category === 'camp').length },
        { id: 'facility', label: 'Facility Updates', count: announcements.filter(a => a.category === 'facility').length },
        { id: 'service', label: 'Service Updates', count: announcements.filter(a => a.category === 'service').length }
    ];

    const filteredAnnouncements = activeCategory === 'all' 
        ? announcements 
        : announcements.filter(announcement => announcement.category === activeCategory);

    const itemsPerPage = 6;
    const totalPages = Math.ceil(filteredAnnouncements.length / itemsPerPage);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentAnnouncements = filteredAnnouncements.slice(startIndex, endIndex);

    const formatDate = (date) => {
        return format(date, 'MMM dd, yyyy');
    };

    const getPriorityIcon = (priority) => {
        switch(priority) {
            case 'high': return <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></div>;
            case 'medium': return <div className="w-2 h-2 rounded-full bg-yellow-500"></div>;
            default: return <div className="w-2 h-2 rounded-full bg-green-500"></div>;
        }
    };

    const handlePrevPage = () => {
        setCurrentPage(prev => Math.max(0, prev - 1));
    };

    const handleNextPage = () => {
        setCurrentPage(prev => Math.min(totalPages - 1, prev + 1));
    };

    return (
        <section id="announcements" className="py-20 bg-gradient-to-b from-gray-50 to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, margin: "-100px" }}
                    className="text-center mb-12"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-blue-100 to-cyan-100 rounded-2xl mb-6">
                        <FaBullhorn className="text-3xl text-blue-600" />
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                        Clinic <span className="text-blue-600">Announcements</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Stay informed about important updates, health alerts, and service changes at Dr. Asrat Medical Clinic
                    </p>
                </motion.div>

                {/* Category Filter */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    viewport={{ once: true }}
                    className="mb-10"
                >
                    <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                onClick={() => {
                                    setActiveCategory(category.id);
                                    setCurrentPage(0);
                                }}
                                className={`px-4 py-2.5 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${
                                    activeCategory === category.id
                                        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                                        : 'bg-white text-gray-700 border border-gray-200 hover:border-blue-300 hover:shadow-md'
                                }`}
                            >
                                {category.label}
                                <span className={`px-2 py-0.5 text-xs rounded-full ${
                                    activeCategory === category.id
                                        ? 'bg-white/20'
                                        : 'bg-gray-100'
                                }`}>
                                    {category.count}
                                </span>
                            </button>
                        ))}
                    </div>
                </motion.div>

                {/* Announcements Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
                    <AnimatePresence mode="wait">
                        {currentAnnouncements.map((announcement, index) => (
                            <motion.div
                                key={announcement.id}
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: -20 }}
                                transition={{ duration: 0.4, delay: index * 0.05 }}
                                layout
                                whileHover={{ y: -5, transition: { duration: 0.2 } }}
                                className={`relative group rounded-2xl border-l-4 ${announcement.color} p-6 bg-white shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden`}
                            >
                                {/* Background Pattern */}
                                <div className="absolute inset-0 opacity-5 group-hover:opacity-10 transition-opacity duration-300">
                                    <div className="absolute -top-4 -right-4 w-16 h-16 rounded-full bg-blue-200"></div>
                                    <div className="absolute -bottom-4 -left-4 w-12 h-12 rounded-full bg-green-200"></div>
                                </div>

                                {/* Header */}
                                <div className="flex items-start justify-between mb-4 relative z-10">
                                    <div className="flex items-center gap-3">
                                        <div className="p-3 rounded-xl bg-white shadow-sm">
                                            {announcement.icon}
                                        </div>
                                        <div>
                                            <div className="flex items-center gap-2">
                                                {getPriorityIcon(announcement.priority)}
                                                <span className={`inline-block px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                                                    announcement.priority === 'high' 
                                                        ? 'bg-red-100 text-red-800'
                                                        : announcement.priority === 'medium'
                                                        ? 'bg-yellow-100 text-yellow-800'
                                                        : 'bg-green-100 text-green-800'
                                                }`}>
                                                    {announcement.badge}
                                                </span>
                                            </div>
                                            <h3 className="text-lg font-bold text-gray-900 mt-1 group-hover:text-blue-700 transition-colors">
                                                {announcement.title}
                                            </h3>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <p className="text-gray-700 mb-5 leading-relaxed relative z-10">
                                    {announcement.description}
                                </p>

                                {/* Details */}
                                <div className="space-y-3 mb-6 relative z-10">
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <FaClock className="w-4 h-4" />
                                        <span className="font-medium">{formatDate(announcement.date)}</span>
                                        <span className="text-gray-400">•</span>
                                        <span className="text-gray-500">
                                            {Math.floor((new Date() - announcement.date) / (1000 * 60 * 60 * 24))} days ago
                                        </span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-600">
                                        <FaMapMarkerAlt className="w-4 h-4" />
                                        <span>{announcement.location}</span>
                                    </div>
                                </div>

                                {/* Action Button */}
                                <button className="w-full py-2.5 bg-gradient-to-r from-gray-50 to-white border border-gray-200 text-gray-700 font-medium rounded-xl hover:border-blue-300 hover:text-blue-600 hover:shadow-md transition-all duration-300 relative z-10 flex items-center justify-center gap-2">
                                    <span>View Details</span>
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                                    </svg>
                                </button>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="flex items-center justify-center gap-4"
                    >
                        <button
                            onClick={handlePrevPage}
                            disabled={currentPage === 0}
                            className={`p-2.5 rounded-xl ${
                                currentPage === 0
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100 hover:shadow-md'
                            } transition-all duration-300`}
                        >
                            <FaChevronLeft className="w-5 h-5" />
                        </button>
                        
                        <div className="flex items-center gap-2">
                            {Array.from({ length: totalPages }, (_, i) => i).map(page => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`w-10 h-10 rounded-xl font-medium transition-all duration-300 ${
                                        currentPage === page
                                            ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg'
                                            : 'text-gray-700 hover:bg-gray-100'
                                    }`}
                                >
                                    {page + 1}
                                </button>
                            ))}
                        </div>
                        
                        <button
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages - 1}
                            className={`p-2.5 rounded-xl ${
                                currentPage === totalPages - 1
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-100 hover:shadow-md'
                            } transition-all duration-300`}
                        >
                            <FaChevronRight className="w-5 h-5" />
                        </button>
                    </motion.div>
                )}

                {/* Emergency Notice */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    viewport={{ once: true }}
                    className="mt-16 bg-gradient-to-r from-red-50 to-orange-50 border-l-4 border-red-500 rounded-2xl p-6 md:p-8 shadow-lg"
                >
                    <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <div className="p-3 bg-white rounded-xl shadow-sm">
                                <FaExclamationTriangle className="text-2xl text-red-600" />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-gray-900 mb-1">Emergency Contact</h3>
                                <p className="text-gray-700">
                                    For medical emergencies, call our 24/7 helpline or visit our emergency department immediately.
                                </p>
                            </div>
                        </div>
                        <div className="text-center md:text-right">
                            <div className="text-2xl md:text-3xl font-bold text-red-600 mb-1">+251-91313-4461</div>
                            <p className="text-gray-600 text-sm">Available 24 hours, 7 days a week</p>
                        </div>
                    </div>
                </motion.div>

                {/* RSS Feed / Subscribe */}
                <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    viewport={{ once: true }}
                    className="mt-12 text-center"
                >
                    <p className="text-gray-600 mb-4">Stay updated with our latest announcements</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="px-4 py-3 bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent flex-grow"
                        />
                        <button className="px-6 py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-semibold rounded-xl hover:shadow-xl transition-all duration-300">
                            Subscribe
                        </button>
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default Announcements;